import { Component, OnInit, Optional, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable, startWith, map } from 'rxjs';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AngularMaterialModule } from '../../angular-material.module';
import { JoinSocietyService } from './join-society.service';

@Component({
  selector: 'app-join-society',
  standalone: true,
  imports: [CommonModule, AngularMaterialModule, ReactiveFormsModule, FormsModule],
  templateUrl: './join-society.component.html',
  styleUrls: ['./join-society.component.scss']
})
export class JoinSocietyComponent implements OnInit, AfterViewInit {

  userID = sessionStorage.getItem('session_id');
  joinSocietyForm: FormGroup;

  societies: any[] = [];
  filteredSocieties!: Observable<any[]>;

  blocks: any[] = [];
  filteredBlocks!: Observable<any[]>;

  floors: any[] = [];
  filteredFloors!: Observable<any[]>;

  units: any[] = [];
  filteredUnits!: Observable<any[]>;

  selectedFile: File | null = null;
  attachmentPreview: string | ArrayBuffer | null = null;
  attachmentName: string | null = null;

  loading = false;

  constructor(
    private fb: FormBuilder,
    private joinSocietyService: JoinSocietyService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef?: MatDialogRef<JoinSocietyComponent>
  ) {
    this.joinSocietyForm = this.fb.group({
      account_id: [null, Validators.required],
      block_id: [null, Validators.required],
      floor_id: [null, Validators.required],
      floor_unit: [null, Validators.required],
      documents: [null]
    });
  }

  ngAfterViewInit(): void {
    // Blur any focused element to prevent autofocus after dialog opens
    setTimeout(() => {
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);
  }

  ngOnInit(): void {
    // Fetch Societies
    this.joinSocietyService.fetchSociety().subscribe(res => {
      this.societies = res;
      this.filteredSocieties = this.joinSocietyForm.get('account_id')!.valueChanges.pipe(
        startWith(''),
        map(value => this._filterDropdown(value, this.societies, 'account_name'))
      );
    });

    // When Society changes, fetch Blocks, Floors, Units dynamically
    this.joinSocietyForm.get('account_id')!.valueChanges.subscribe(selectedSoc => {
      if (selectedSoc && selectedSoc.account_id) {
        // Fetch Blocks
        this.joinSocietyService.fetchBlocks(selectedSoc.account_id).subscribe(blocksRes => {
          this.blocks = blocksRes.map(b => ({ id: b.block_id, name: b.block_name }));
          this.filteredBlocks = this.joinSocietyForm.get('block_id')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterDropdown(value, this.blocks, 'name'))
          );
          this.joinSocietyForm.patchValue({ block_id: null });
        });

        // Fetch Floors
        this.joinSocietyService.fetchFloors(selectedSoc.account_id).subscribe(floorsRes => {
          this.floors = floorsRes.map(f => ({ id: f.floor_id, name: f.floor_name }));
          this.filteredFloors = this.joinSocietyForm.get('floor_id')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterDropdown(value, this.floors, 'name'))
          );
          this.joinSocietyForm.patchValue({ floor_id: null });
        });

        // Fetch Units
        this.joinSocietyService.fetchUnits(selectedSoc.account_id).subscribe(unitsRes => {
          this.units = unitsRes.map(u => ({ id: u.floor_unit_id, name: u.floor_unit }));
          this.filteredUnits = this.joinSocietyForm.get('floor_unit')!.valueChanges.pipe(
            startWith(''),
            map(value => this._filterDropdown(value, this.units, 'name'))
          );
          this.joinSocietyForm.patchValue({ floor_unit: null });
        });
      }
    });
  }

  private _filterDropdown(value: any, options: any[], key: string): any[] {
    if (!value) return options;
    const filterValue = typeof value === 'string' ? value.toLowerCase() : value[key].toLowerCase();
    return options.filter(opt => opt[key].toLowerCase().includes(filterValue));
  }

  // Display functions for autocomplete
  displaySociety(society: any): string { return society?.account_name || ''; }
  displayBlock(block: any): string { return block?.name || ''; }
  displayFloor(floor: any): string { return floor?.name || ''; }
  displayUnit(unit: any): string { return unit?.name || ''; }


    onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      if (this.selectedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => {
          this.attachmentPreview = reader.result;
        };
        reader.readAsDataURL(this.selectedFile);
      } else {
        this.attachmentPreview = null;
      }
    }
  }

  deleteFile() {
    this.selectedFile = null;
    this.attachmentName = null;
    this.attachmentPreview = null;
    this.joinSocietyForm.patchValue({ documents: null });
  }

  onSubmit(formDirective: FormGroupDirective) {
    if (!this.joinSocietyForm.valid) return;

    this.loading = true;

    const formData = new FormData();
    const userId = this.userID || '';
    formData.append('user_id', userId);
    formData.append('account_id', this.joinSocietyForm.value.account_id.account_id);
    formData.append('block_id', this.joinSocietyForm.value.block_id.id);
    formData.append('floor_id', this.joinSocietyForm.value.floor_id.id);
    formData.append('floor_unit_id', this.joinSocietyForm.value.floor_unit.id);
    formData.append('created_by', userId);

    if (this.selectedFile) {
      formData.append('society_attachment', this.selectedFile);
    }

    this.joinSocietyService.joinSociety(formData).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.success) {
          this.snackBar.open('Joined Society Successfully ✅', 'Close', { duration: 3000 });
          formDirective.resetForm();
          this.joinSocietyForm.reset();
          this.selectedFile = null;
          this.attachmentPreview = null;
        } else {
          this.snackBar.open('Failed to join society ❌', 'Close', { duration: 3000 });
        }
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Failed to join society ❌', 'Close', { duration: 3000 });
      }
    });
  }

  closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close(false);
    }
  }
}
